import type { ThreeEvent } from '@react-three/fiber'
import { useAppSelector } from 'common/hooks/redux'
import { ElementIdLabel } from 'features/Procedure/procedure-model/element-id-label'
import { type DrawingElement } from 'features/Procedure/state/procedure.type'
import { formatDrawingNoteDisplay } from 'features/Procedure/utils/drawingDescription.utils'
import { getProcedureOpacity } from 'features/Procedure/utils/utils'
import React, { useMemo, useRef } from 'react'
import type { MeshStandardMaterial } from 'three'
import { Vector3 } from 'three'

type Props = {
  debugMode?: boolean
  element: DrawingElement
  isSelected?: boolean
  isFromSelectedProcedure?: boolean
  showElementIds?: boolean
  onClick?: (event: ThreeEvent<PointerEvent>) => void
  onPointerDown?: (event: ThreeEvent<PointerEvent>) => void
  onPointerEnter?: (event: ThreeEvent<PointerEvent>) => void
  onPointerOver?: (event: ThreeEvent<PointerEvent>) => void
  useDepthTest?: boolean
  inactiveOpacity?: number
}

export const Pin = ({
  element,
  debugMode = false,
  isSelected = false,
  isFromSelectedProcedure = false,
  showElementIds: showElementIdsOverride,
  onClick,
  onPointerDown,
  onPointerEnter,
  onPointerOver,
  useDepthTest = false,
  inactiveOpacity = 0,
}: Props) => {
  const storeShowElementIds = useAppSelector((state) => state.procedureReducer.showElementIds)
  const isPrinting = useAppSelector((state) => state.procedureReducer.isPrinting)
  const materialRef = useRef<MeshStandardMaterial>(null)
  const showElementIds = showElementIdsOverride ?? storeShowElementIds

  React.useEffect(() => {
    if (debugMode) {
      console.log(`Pin ${element.id} selection state:`, {
        isSelected,
        isFromSelectedProcedure,
      })
    }
  }, [isSelected, isFromSelectedProcedure, element.id, debugMode])

  React.useEffect(() => {
    if (materialRef.current) {
      materialRef.current.transparent = !isFromSelectedProcedure && !isPrinting
      materialRef.current.opacity = getProcedureOpacity(
        isFromSelectedProcedure,
        isPrinting,
        false,
        inactiveOpacity,
      )
      materialRef.current.depthTest = useDepthTest
      materialRef.current.needsUpdate = true
    }
  }, [isFromSelectedProcedure, isPrinting, useDepthTest, inactiveOpacity])

  const pinPosition = new Vector3(element.points[0].x, element.points[0].y, element.points[0].z)

  const descriptionText = useMemo(
    () => formatDrawingNoteDisplay(element.description),
    [element.description],
  )

  const handleClick = (event: ThreeEvent<PointerEvent>) => {
    if (debugMode) console.log('Pin clicked:', element.id, 'Currently selected:', isSelected)

    if (onClick) {
      event.stopPropagation()
      onClick(event)
    }
  }

  const handlePointerDown = (event: ThreeEvent<PointerEvent>) => {
    onPointerDown?.(event)
  }

  const handlePointerEnter = (event: ThreeEvent<PointerEvent>) => {
    onPointerEnter?.(event)
  }

  const handlePointerOver = (event: ThreeEvent<PointerEvent>) => {
    onPointerOver?.(event)
  }

  const radius = element.size || 0.25

  return (
    <group position={pinPosition}>
      <mesh
        onClick={handleClick}
        onPointerDown={handlePointerDown}
        onPointerEnter={handlePointerEnter}
        onPointerOver={handlePointerOver}>
        <sphereGeometry args={[radius, 32, 32]} />
        <meshStandardMaterial
          ref={materialRef}
          color={element.color}
          emissive={element.color}
          emissiveIntensity={isSelected ? 0.3 : 0.1}
          transparent={!isFromSelectedProcedure && !isPrinting}
          opacity={getProcedureOpacity(isFromSelectedProcedure, isPrinting, false, inactiveOpacity)}
          depthTest={useDepthTest}
          depthWrite={false}
        />
      </mesh>
      {showElementIds &&
        descriptionText &&
        (showElementIdsOverride === true || isFromSelectedProcedure) && (
          <ElementIdLabel
            text={descriptionText}
            position={[0, radius, 0]}
            color={element.color}
            useDepthTest={false}
            onClick={onClick}
            onPointerDown={onPointerDown}
            onPointerEnter={onPointerEnter}
            onPointerOver={onPointerOver}
            offset={{
              x:
                Math.abs(pinPosition.x) > Math.abs(pinPosition.z)
                  ? pinPosition.x > 0
                    ? 0.8
                    : -0.8
                  : 0,
              y: 0.25,
              z:
                Math.abs(pinPosition.z) >= Math.abs(pinPosition.x)
                  ? pinPosition.z >= 0
                    ? 0.8
                    : -0.8
                  : 0,
            }}
          />
        )}
    </group>
  )
}
